import { useCallback, useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  CATEGORIES,
  useConsent,
  useOpenPreferencesListener,
  type ConsentChoices,
} from "@/lib/consent";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DEFAULT: ConsentChoices = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export function CookieBanner() {
  const { state, hasDecision, acceptAll, rejectAll, save } = useConsent();
  const [mounted, setMounted] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>(state?.choices ?? DEFAULT);

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (state?.choices) setChoices(state.choices);
  }, [state?.timestamp]);

  const openPrefs = useCallback(() => {
    setChoices(state?.choices ?? DEFAULT);
    setPrefsOpen(true);
  }, [state?.timestamp]);
  useOpenPreferencesListener(openPrefs);

  if (!mounted) return null;

  const showBanner = !hasDecision && !prefsOpen;

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-title"
          className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 shadow-2xl backdrop-blur"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:gap-6 md:px-6">
            <div className="flex-1 text-sm text-foreground/85">
              <p id="cookie-banner-title" className="font-medium text-foreground">
                Usamos cookies
              </p>
              <p className="mt-1 leading-relaxed">
                Utilizamos cookies técnicas imprescindibles y, con tu permiso, cookies para
                análisis, preferencias y contenido embebido. Puedes aceptarlas todas,
                rechazarlas o configurarlas. Consulta la{" "}
                <Link to="/legal/cookies" className="underline underline-offset-2">
                  política de cookies
                </Link>{" "}
                y la{" "}
                <Link to="/legal/privacidad" className="underline underline-offset-2">
                  política de privacidad
                </Link>
                . Responsable: CASA JUANA Y LUCIO S.L. (CIF B85745362).
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row md:flex-col md:items-stretch lg:flex-row">
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="min-w-32"
              >
                Rechazar todas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={openPrefs}
                className="min-w-32"
              >
                Configurar
              </Button>
              <Button size="sm" onClick={acceptAll} className="min-w-32">
                Aceptar todas
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Preferencias de cookies</DialogTitle>
            <DialogDescription>
              Activa o desactiva cada categoría. Las cookies necesarias no pueden desactivarse
              porque son imprescindibles para el funcionamiento del sitio.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className="flex items-start justify-between gap-4 rounded-md border border-border p-3"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{cat.label}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {cat.description}
                  </p>
                </div>
                <Switch
                  checked={cat.always ? true : choices[cat.key]}
                  disabled={cat.always}
                  onCheckedChange={(v) =>
                    setChoices((prev) => ({ ...prev, [cat.key]: v }))
                  }
                  aria-label={`Consentimiento para cookies de ${cat.label}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                rejectAll();
                setPrefsOpen(false);
              }}
            >
              Rechazar todas
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  save(choices);
                  setPrefsOpen(false);
                }}
              >
                Guardar preferencias
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  acceptAll();
                  setPrefsOpen(false);
                }}
              >
                Aceptar todas
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}